import React, { FocusEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CircularProgress, Popper } from '@mui/material'

import { SearchInputWithDebounce } from '../../../../components/SearchInputWithDebounce/SearchInputWithDebounce'
import { PromiseWithAbort } from '../../../../types/PromiseWithAbort'
import { GameSearchDialogTab, GameSearchDialogType } from '../../../game-search/types/GameSearchDialogConfig'
import { SearchGamesQueryParams } from '../../../game-search/types/SearchGamesQueryParams'
import { useGameSearch } from '../../../game/hooks/gameHooks'
import { Game } from '../../../game/types/Game'
import { useCurrentMarket } from '../../../markets'
import GameSearchDialog from '../GameSearchDialog/GameSearchDialog'
import GameSearchRecentResult from '../GameSearchRecentResult/GameSearchRecentResult'
import GameSearchResult from '../GameSearchResult/GameSearchResult'
import './GameSearchInput.scss'

const GameSearchInput: React.FC = () => {
  const SEARCH_TEXT_MIN_LENGTH = 2
  const { t } = useTranslation()
  const { currentMarketIso: mainMarketIso } = useCurrentMarket()
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(undefined)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const {
    searchGames,
    result: { data: searchedGames, isLoading: isLoadingSearchedGames },
  } = useGameSearch()
  const [searchGamesPromise, setSearchGamesPromise] = useState<PromiseWithAbort>(undefined)
  const [previousMarketIso, setPreviousMarketIso] = useState(mainMarketIso)
  const [advancedSearchModalOpen, setAdvancedSearchModalOpen] = useState(false)
  const gameSearchDialogConfig = useMemo(() => {
    return {
      type: GameSearchDialogType.GameSearch,
      tabs: [GameSearchDialogTab.Search],
    }
  }, [])

  // When the market is switched from the header, clear any active search term
  // so stale results from the previous market are not shown. The visible input
  // lives in SearchInputWithDebounce's own state and is reset via the `key`
  // prop below. Because that remounts the input (the popover's anchor), we also
  // close the popover and drop the stale anchor so Popper isn't left pointing at
  // an unmounted node, and abort any in-flight search.
  useEffect(() => {
    if (mainMarketIso && previousMarketIso && mainMarketIso !== previousMarketIso) {
      setPreviousMarketIso(mainMarketIso)
      setSearchText('')
      setPopoverOpen(false)
      setAnchorEl(undefined)
      if (searchGamesPromise) {
        searchGamesPromise.abort()
        setSearchGamesPromise(undefined)
      }
    }
  }, [mainMarketIso, previousMarketIso, searchGamesPromise])

  const onSearchTextChange = (newSearchText: string) => {
    setSearchText(newSearchText)
    if (searchGamesPromise) {
      searchGamesPromise.abort()
    }

    if (newSearchText.length >= SEARCH_TEXT_MIN_LENGTH) {
      const searchGamesParams: Omit<SearchGamesQueryParams, 'userLanguage'> = {
        term: newSearchText,
        marketIso: mainMarketIso,
        include: 'name,artist,icon,icons,gpsPerMarket,visibility,genreId,internal,owner,archive,stageId,sranks',
        owned: false,
      }

      const newSearchGamesPromise = searchGames(searchGamesParams)
      setSearchGamesPromise(newSearchGamesPromise)
    }
  }

  const openPopover = (event: FocusEvent<any>) => {
    setAnchorEl(event.target)
    setPopoverOpen(true)
  }

  const closePopoverWithDelay = () => {
    setTimeout(() => {
      setPopoverOpen(false)
    }, 200)
  }

  const toggleGameSearchDialog = () => {
    setAdvancedSearchModalOpen(!advancedSearchModalOpen)
  }

  const games = useMemo(() => {
    if (searchText.length < SEARCH_TEXT_MIN_LENGTH) return []
    if (!searchedGames || !searchedGames.length) return []

    return searchedGames
  }, [searchText, searchedGames])

  return (
    <div className="GameSearchInput">
      <div className="GameSearchInput__input-wrapper">
        <SearchInputWithDebounce
          key={mainMarketIso}
          fieldText={t('common:search_games')}
          onDebounce={onSearchTextChange}
          onFocus={openPopover}
          onBlur={closePopoverWithDelay}
          onOpenGameSearchDialog={toggleGameSearchDialog}
          hasIconChange
        />
        <GameSearchDialog
          gameSearchDialogConfig={gameSearchDialogConfig}
          modalOpen={advancedSearchModalOpen}
          onClose={toggleGameSearchDialog}
          hideSetAsDefault
          enableMarketSelector
          hasDefaultSubgenre
        />
      </div>
      <Popper
        className="GameSearchInput__popper"
        open={popoverOpen}
        anchorEl={anchorEl}
        // The Search games box lives inside the sticky AppHeader; using the
        // default `absolute` strategy makes the dropdown lag behind during
        // page scroll. `fixed` positions the popper in viewport coordinates
        // so it stays anchored directly under the input. (GR-2895)
        popperOptions={{ strategy: 'fixed' }}
      >
        <div className="GameSearchInput__popper__content">
          {searchText.length === 0 && <GameSearchRecentResult />}
          {isLoadingSearchedGames && (
            <div className="text-center" style={{ margin: '20px auto' }}>
              <CircularProgress color="primary" />
            </div>
          )}
          {!isLoadingSearchedGames && searchText.length > 0 && (
            <div className="GameSearchInput__popper__result-message">
              {t('common:found_matching_the_search', {
                count: games.length,
              })}
            </div>
          )}
          {!isLoadingSearchedGames &&
            searchText.length > 0 &&
            games &&
            games.map((game: Game) => {
              return <GameSearchResult key={game.id} game={game} />
            })}
        </div>
      </Popper>
    </div>
  )
}

export default GameSearchInput
